import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import {
  amountToNumber,
  fieldsToGqlString,
  mergeByIdNewAtTop,
} from '../../../util/shared/vue-global';
import { toFiscalYear, toShortDate } from '../../../util/shared/tmc-global';

const { backend } = clients.direct;

// A single business day never holds this many GL rows; the glDailyTransaction
// resolver's aggregation requires numeric $skip/$limit (it throws on null), so we
// page through every row for the day.
const LOCK_CHECK_LIMIT = 100000;

const defaultTreasurerCheckFields = {
  _id: '',
  bankId: '',
  bankName: '',
  date: '',
  checkNo: '',
  payee: '',
  amount: '',
  paidDate: '',
  forWhat: '',
};

// Builds the paired Journal Entry rows for a treasurer check. Both rows are
// transfer-outs so the entry balances: one on the bank the check was drawn from,
// one on the target fund (defaulting to 7408 Tax Refunds).
export const buildTreasurerCheckJERows = ({ check, fundId }) => {
  const amount = amountToNumber(check.amount);
  const fiscalYear = check.date ? toFiscalYear(new Date(check.date)) : null;
  const businessDate = check.date;
  const description = [
    `TREASURER CHECK #${check.checkNo || ''} ${check.payee || ''}`.trim(),
    check.forWhat,
  ].filter(Boolean).join('\n');
  const checkNo = check.checkNo || null;
  const bankRow = {
    fiscalYear,
    businessDate,
    dailySection: 'JOURNALENTRIES',
    bank: check.bankId,
    transferOut: amount,
    description,
    fromNumber: checkNo,
    toNumber: checkNo,
  };
  const fundRow = {
    fiscalYear,
    businessDate,
    dailySection: 'JOURNALENTRIES',
    fund: fundId,
    transferOut: amount,
    description,
    fromNumber: checkNo,
    toNumber: checkNo,
  };
  return [bankRow, fundRow];
};

export const treasurersChecks = {
  namespaced: true,
  state: () => ({
  }),
  mutations: {
  },
  actions: {
    async loadTreasurersChecks({ commit, dispatch }, {
      criteria, fields, baseCrudKey,
    }) {
      let results;
      const gqlfields = fields || defaultTreasurerCheckFields;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query treasurersChecks($criteria: TreasurerCheckCriteriaType) {
            treasurersChecks(criteria: $criteria) {
              ${fieldsToGqlString(gqlfields)},
            }
          }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const list = results.data ? results.data.treasurersChecks || [] : [];
        commit('base/crud/setItems', [baseCrudKey, list], { root: true });
        return list;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem loading the Treasurers Checks list.', { root: true });
      }
      return [];
    },
    async upsertTreasurerCheck({ dispatch, rootGetters, commit }, {
      item, fields, baseCrudKey,
    }) {
      let results;
      try {
        const gqlfields = fields || defaultTreasurerCheckFields;
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTreasurerCheck($item: TreasurerCheckInputType!) {
            upsertTreasurerCheck(upsert: $item) {
              ${fieldsToGqlString(gqlfields)},
            }
          }`,
          variables: { item },
        });
        clients.handleGQLErrors(results, commit);
        const stateData = rootGetters['base/crud/items'](baseCrudKey);
        const newItem = results.data ? (results.data.upsertTreasurerCheck || {}) : {};
        const newList = mergeByIdNewAtTop(stateData || [], newItem);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        return newItem;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in saving the Treasurer Check!', { root: true });
      }
      return {};
    },
    async isGlDayLocked({ dispatch, commit }, { fiscalYear, businessDate }) {
      if (!fiscalYear || !businessDate) return false;
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`query glDailyTransaction($criteria: glDailyTransactionCriteriaType) {
            glDailyTransaction(criteria: $criteria) {
              _id
              isLocked
            }
          }`,
          // Range over the whole day so a stored time-of-day component still matches.
          // skip/limit are required: the resolver's aggregation builds a $skip stage
          // and throws ("$skip: null") when they are omitted.
          variables: {
            criteria: {
              fiscalYear,
              fromBusinessDate: businessDate,
              toBusinessDate: businessDate,
              skip: 0,
              limit: LOCK_CHECK_LIMIT,
            },
          },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        clients.handleGQLErrors(results, commit);
        const dayRows = results && results.data ? (results.data.glDailyTransaction || []) : [];
        return dayRows.some((r) => r.isLocked);
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
      }
      // Fail open: if lock state cannot be determined, do not block JE creation.
      return false;
    },
    async createJournalEntriesForCheck({ dispatch, commit }, { check, fundId }) {
      let results;
      const createdIds = [];
      try {
        // A date is required to derive the fiscal year / business date of the JE.
        if (!check.date) {
          dispatch('flashError', 'A check date is required to create journal entries.', { root: true });
          return false;
        }
        await dispatch('reAuth', null, { root: true });
        const rows = buildTreasurerCheckJERows({ check, fundId });
        const { fiscalYear, businessDate } = rows[0];
        // Do not post journal entries into a day whose GL records are locked.
        const locked = await dispatch('isGlDayLocked', { fiscalYear, businessDate });
        if (locked) {
          dispatch('flashWarn', `Could not create JE since records for ${toShortDate(new Date(businessDate))} are locked.`, { root: true });
          return false;
        }
        const mutation = gql`mutation upsertGlDailyTransaction($item: glDailyTransactionInputType!) {
          upsertGlDailyTransaction(upsert: $item) {
            _id
          }
        }`;
        // Create sequentially, tracking ids so a mid-way failure can be rolled back
        // (both rows must exist or neither, otherwise the entry is unbalanced).
        for (let i = 0; i < rows.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          results = await backend.mutate({ mutation, variables: { item: rows[i] } });
          clients.handleGQLErrors(results, commit);
          const createdId = results.data && results.data.upsertGlDailyTransaction
            ? results.data.upsertGlDailyTransaction._id : null;
          if (!createdId) {
            throw new Error('A journal entry row was not created.');
          }
          createdIds.push(createdId);
        }
        dispatch('flashSuccess', 'Journal entries created for the treasurer check.', { root: true });
        return true;
      } catch (e) {
        await dispatch('rollbackJournalEntries', createdIds);
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        const rolledBack = createdIds.length > 0 ? ' Any partial entries were rolled back.' : '';
        dispatch('flashError', `There was a problem creating journal entries for the treasurer check.${rolledBack}`, { root: true });
      }
      return false;
    },
    async rollbackJournalEntries({ dispatch }, createdIds) {
      if (!Array.isArray(createdIds) || createdIds.length === 0) return;
      const mutation = gql`mutation deleteGlDailyTransaction($id: ID!) {
        deleteGlDailyTransaction(id: $id) {
          _id
        }
      }`;
      for (let i = 0; i < createdIds.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await backend.mutate({ mutation, variables: { id: createdIds[i] } });
        } catch (rollbackErr) {
          // Best effort: a failed rollback is logged but must not mask the original error.
          dispatch('logException', rollbackErr, { root: true });
        }
      }
    },
    async deleteTreasurerCheck({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteTreasurerCheck($id: ID!) {
            deleteTreasurerCheck(id: $id) {
              _id
            }
          }`,
          variables: { id: item._id },
        });
        clients.handleGQLErrors(results, commit);
        const delId = results.data ? (results.data.deleteTreasurerCheck || {})._id : null;
        const stateData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = stateData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        commit('base/crud/setIsVisible', [baseCrudKey, false], { root: true });
        return delId;
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem while deleting the Treasurer Check!', { root: true });
      }
      return null;
    },
  },
};

export default {
  treasurersChecks,
};
